import { apiFetch, ApiError, getToken, setToken } from './apiClient';

export interface ArcanumSession {
  user: { uid: string; email: string };
  isArcanumTeam: boolean;
  /** null se non è team Arcanum. Usato per gate azioni admin-only (es. eliminare un lead). */
  arcanumLevel: string | null;
}

type Listener = (session: ArcanumSession | null) => void;
const listeners = new Set<Listener>();
let current: ArcanumSession | null | undefined;

function publish(session: ArcanumSession | null): void {
  current = session;
  if (session) startTokenRefresh(); else stopTokenRefresh();
  listeners.forEach((l) => l(session));
}

/**
 * Il token dell'API vale un'ora: senza rinnovo il CRM si chiudeva da solo a metà di un lavoro (audit CTO 26/10/2026), come era già
 * successo alla piattaforma il 20/10. Stesso schema di useSessionSync: POST /auth/refresh ogni 45s mentre la scheda è visibile e al ritorno
 * sulla scheda; un 401 chiude la sessione, un errore di rete si riprova al giro dopo.
 */
export const TOKEN_REFRESH_INTERVAL_MS = 45_000;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

async function refreshToken(): Promise<void> {
  if (!getToken() || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
  try {
    const { token } = await apiFetch<{ token: string }>('/auth/refresh', { method: 'POST' });
    setToken(token);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { setToken(null); publish(null); }
  }
}

function onVisible(): void { if (document.visibilityState === 'visible') void refreshToken(); }

function startTokenRefresh(): void {
  if (refreshTimer) return;
  refreshTimer = setInterval(() => { void refreshToken(); }, TOKEN_REFRESH_INTERVAL_MS);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
}

function stopTokenRefresh(): void {
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
}

/** Legge il profilo dell'utente collegato (GET /users/me) e ne ricava la sessione del CRM. */
async function loadSession(): Promise<ArcanumSession | null> {
  if (!getToken()) return null;
  try {
    const me = await apiFetch<{ id: string; email: string; arcanum_level: string | null }>('/users/me');
    return { user: { uid: me.id, email: me.email }, isArcanumTeam: !!me.arcanum_level, arcanumLevel: me.arcanum_level ?? null };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) setToken(null);
    return null;
  }
}

export async function signIn(email: string, password: string): Promise<void> {
  const { token } = await apiFetch<{ token: string }>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  setToken(token);
  publish(await loadSession());
}

export async function signOut(): Promise<void> {
  setToken(null);
  publish(null);
}

/**
 * Il CRM è riservato al team Arcanum: la sessione porta arcanumLevel dal profilo (stessa regola della piattaforma).
 * Chiama subito il callback con lo stato corrente (dopo averlo letto la prima volta) e poi a ogni login/logout.
 */
export function subscribeToSession(callback: Listener): () => void {
  listeners.add(callback);
  if (current !== undefined) callback(current);
  else void loadSession().then((s) => { if (current === undefined) publish(s); });
  return () => { listeners.delete(callback); };
}
